import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrCheckboxList from '../../../common/arr-checkbox-list/ArrCheckboxList';
import {ISelectItem} from '../../../../shared/types';
import {t} from 'i18next';
import {ArrTooltipTitle} from '../../../common/arr-tooltip-title/ArrTooltipTitle';
import './QuickBatch.scss';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {numberFormatter} from '../../../../utils/utils';

// Types Definitions
// ----------------------------------
export interface IOptions {
  label: string;
  value: boolean;
}
// ------------------

export interface ISortOptions {
  label: string;
  value: string;
}
// ------------------

type ICodingValue = {id: string; name: string} | null;
interface IQuickBatchProps {
  totalSelected: number;
  codingFormOptions: ISelectItem[];
  sortOptions: ISortOptions[];
  reviewerOptions: IOptions[];
  onAssignNameChange: (value: string) => void;
  onCodingFormChange: (value: ICodingValue) => void;
  onSortChange: (value: string) => void;
  onReviewerChange: (reviewerValues: IOptions[]) => void;
  startingCount: number;
  isStartingCountLoading?: boolean;
  assignmentName: string;
  isAssignmentValid: boolean;
  handleBlurAssignName: (fieldName: 'assignmentName') => void;
  setErrorMsg: () => string;
}
// ------------------

const enQuickBatch = 'actionMenu.quickBatchFlyout';

// ----------------------------------
// QuickBatch Component
// ----------------------------------
const QuickBatch: React.FC<IQuickBatchProps> = ({
  codingFormOptions,
  sortOptions,
  reviewerOptions,
  onAssignNameChange,
  onCodingFormChange,
  onSortChange,
  onReviewerChange,
  startingCount = 0,
  isStartingCountLoading = true,
  assignmentName,
  isAssignmentValid,
  handleBlurAssignName,
  setErrorMsg,
}) => {
  const [sortValue, setSortValue] = useState<string>(sortOptions[0].value);
  const [reviewers, setReviewers] = useState<IOptions[]>(reviewerOptions);
  const [codingFormValue, setCodingFormValue] = useState<ICodingValue>(null);
  // ------------------

  const handleSortChange = (value: string) => {
    setSortValue(value);

    if (onSortChange) {
      onSortChange(value);
    }
  };
  // ------------------

  const handleCodingFormChange = (value: ISelectItem | null) => {
    const codingValue: ICodingValue = value
      ? {id: String(value.id), name: String(value.name ?? '')}
      : null;

    setCodingFormValue(codingValue);

    if (onCodingFormChange) {
      onCodingFormChange(codingValue);
    }
  };
  // ------------------

  const handleReviewerChange = (values: boolean[]) => {
    const reviewerValues = values.map((value, i) => ({...reviewers[i], value}));
    setReviewers(reviewerValues);

    if (onReviewerChange) {
      onReviewerChange(reviewerValues);
    }
  };
  // ------------------

  useEffect(() => {
    setReviewers(reviewerOptions);
  }, [reviewerOptions]);

  return (
    <Box className="quick-batch">
      {/* Inputs */}
      <Box className="quick-batch__section">
        <Box className="quick-batch-total-docs" tabIndex={0}>
          <Typography
            component="span"
            className="typo-text subhead4_size section-title"
            data-testid="quick-batch-total-docs-desc"
          >
            {t(`${enQuickBatch}.totalDocs`)}:
          </Typography>
          <Typography
            component="span"
            className="typo-text subhead4_size text-info"
            data-testid="quick-batch-total-docs"
          >
            {isStartingCountLoading || startingCount <= 0 ? (
              <ArrLoadingSpinner className="arr-loading-small" />
            ) : (
              numberFormatter.format(startingCount)
            )}
          </Typography>
        </Box>
        <Box className="quick-batch__inputs">
          <Box
            className="input"
            data-testid="quick-batch-assignmentName"
            tabIndex={0}
          >
            <ArrTooltipTitle
              dataTestId="assignment-qb"
              title={t(`${enQuickBatch}.assignmentName`)}
              classNames={{
                title: 'typo-text subhead4_size label',
                tooltipIcon: 'input-tooltip',
              }}
              tooltipContent={t(`${enQuickBatch}.tooltip.assignmentName`)}
            />
            <ArrTextField
              state={isAssignmentValid ? 'error' : 'enabled'}
              showErrorIcon={isAssignmentValid}
              sizeVariant="small"
              value={assignmentName}
              placeholder={t(`${enQuickBatch}.assignmentNamePlaceholder`)}
              onChange={(e) => onAssignNameChange(e.target.value)}
              onBlur={() => handleBlurAssignName('assignmentName')}
              dataTestId="quick-batch-assignmentName-input"
              supportText={setErrorMsg()}
            />
          </Box>
          <Box
            className="input"
            data-testid="quick-batch-codingForm"
            tabIndex={0}
          >
            <ArrTooltipTitle
              dataTestId="coding-form-qb"
              title={t(`${enQuickBatch}.codingForm`)}
              classNames={{
                title: 'typo-text subhead4_size label',
                tooltipIcon: 'input-tooltip',
              }}
              tooltipContent={t(`${enQuickBatch}.tooltip.codingForm`)}
            />
            <ArrDropdown
              sizeVariant="small"
              dropdownLabel={t(`${enQuickBatch}.codingFormPlaceholder`)}
              selectedValues={codingFormValue}
              dropdownItems={codingFormOptions}
              onSelectionChange={handleCodingFormChange}
              dataTestId="quick-batch-codingForm-dropdown"
            />
          </Box>
        </Box>
      </Box>

      {/* Sort */}
      <Box className="quick-batch__section" tabIndex={0}>
        <ArrTooltipTitle
          dataTestId="sort-order-qb"
          title={t(`${enQuickBatch}.sortOrder`)}
          classNames={{
            title: 'typo-text subhead4_size section-title',
            tooltipIcon: 'sort-tooltip',
          }}
          tooltipContent={t(`${enQuickBatch}.tooltip.sortOrder`)}
        />
        <Box className="quick-batch__inputs">
          <Box className="input" data-testid="quick-batch-sort-order">
            <ArrRadio
              dataTestId="quick-batch-sort-order-radio"
              name="controlled-radio-buttons-group"
              size="xs"
              value={sortValue}
              onChange={handleSortChange}
              data={sortOptions}
              className="sort-radio"
            />
          </Box>
        </Box>
      </Box>

      {/* Reviewers */}
      <Box className="quick-batch__section" tabIndex={0}>
        <ArrTooltipTitle
          dataTestId="add-reviewers-qb"
          title={t(`${enQuickBatch}.addReviewers`)}
          classNames={{
            title: 'typo-text subhead4_size section-title',
            tooltipIcon: 'sort-tooltip',
          }}
          tooltipContent={t(`${enQuickBatch}.tooltip.addReviewers`)}
        />
        <Box
          className="quick-batch__inputs reviewers"
          data-testid="quick-batch-reviewers"
        >
          <Box className="reviewers-scroll">
            <ArrCheckboxList
              parentLabel={t(`${enQuickBatch}.selectAll`)}
              childLabels={reviewers}
              onChange={handleReviewerChange}
              size="small"
              tabIndex={-1}
              checkboxTestId={'QB-Select-all-reviewers'}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export {QuickBatch};
export default QuickBatch;
